import { motion } from "framer-motion";
import { Globe, Bot, TrendingUp, Megaphone, Magnet, HeartHandshake } from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "Websites & e-commerce",
    copy: "Fast, dependable sites and stores that make small businesses look established from day one.",
  },
  {
    icon: Bot,
    title: "AI tools for operations",
    copy: "Practical AI tools for quoting, sorting, summarising and chasing. Less admin for you, including logistics and supply chain workflows.",
  },
  {
    icon: TrendingUp,
    title: "SEO foundations",
    copy: "Get found on Google and inside AI answers, with clean structure and content built to rank.",
  },
  {
    icon: Megaphone,
    title: "Social media optimisation",
    copy: "Profiles, posting schedules and links that turn scrollers into real enquiries.",
  },
  {
    icon: Magnet,
    title: "Lead generation",
    copy: "Simple funnels and outreach assets that put your business in front of the right people.",
  },
  {
    icon: HeartHandshake,
    title: "Ongoing care",
    copy: "Updates, backups, fixes and straight advice. Your site stays in good hands.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const Services = () => (
  <section id="services" className="relative border-t border-jade/10 bg-paper2/50 py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.p
        {...fadeUp}
        transition={{ duration: 0.6 }}
        className="text-xs font-medium uppercase tracking-[0.3em] text-jade"
      >
        What I do
      </motion.p>
      <motion.h2
        {...fadeUp}
        transition={{ duration: 0.7, delay: 0.08 }}
        className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
        data-testid="services-heading"
      >
        More than a website. A complete <em className="italic text-jade">growth setup</em>.
      </motion.h2>
      <motion.p
        {...fadeUp}
        transition={{ duration: 0.7, delay: 0.14 }}
        className="mt-5 max-w-xl leading-relaxed text-ink2"
        data-testid="services-intro"
      >
        I build the digital side of small businesses across the UK and
        beyond. The site, the systems behind it, and the visibility that
        brings customers in.
      </motion.p>

      <motion.div
        {...fadeUp}
        transition={{ duration: 0.7, delay: 0.18 }}
        className="mt-12 overflow-hidden rounded-2xl shadow-ambient"
        data-testid="services-visual"
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/3f7ef5a3-e08e-4053-bc90-55479d570c9f/images/706dc96771b2ddbaf40d2db3c433ed677df1883e6df478e9e9631e4c778fcc2f.jpeg"
          alt="Abstract ink-brush illustration of an AI tool connecting nodes for a small business"
          className="h-64 w-full object-cover sm:h-80"
        />
      </motion.div>

      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((service, i) => (
          <motion.div
            key={service.title}
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.06 * i }}
            className="group rounded-2xl border border-jade/10 bg-white p-8 shadow-ambient transition-colors duration-300 hover:bg-paper2/60"
            data-testid={`service-card-${i}`}
          >
            <service.icon
              className="h-6 w-6 text-jade transition-transform duration-300 group-hover:-translate-y-0.5"
              strokeWidth={1.75}
            />
            <h3 className="mt-5 font-display text-xl font-medium text-ink">
              {service.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-ink2/80">
              {service.copy}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Services;